// Organic / Minimalist portfolio page — monolithic iframe block
// Single-color, nature/organic theme. No brand switching.

export interface OrganicPortfolioPageData {}
export const DEFAULT_ORGANIC_PORTFOLIO_DATA: OrganicPortfolioPageData = {}

// ── CSS ───────────────────────────────────────────────────────────────────────
const ORGANIC_PORTFOLIO_CSS = `
/* ============ БАЗА / ТЕМЫ ============ */
:root{
  --bg:#F3F0E8; --ink:#1C1A0C; --accent:#3D6B35; --on-accent:#FFFFFF;
  --line:rgba(28,26,12,.15); --muted:#6B6854; --card:#E6E1D2;
  --font-d:'Archivo',system-ui,sans-serif; --font-m:'IBM Plex Mono',monospace;
  --head-h:64px;
}

*{margin:0;padding:0;box-sizing:border-box}
html{scroll-behavior:smooth}
body{
  background:var(--bg); color:var(--ink);
  font-family:var(--font-d); font-size:16px; line-height:1.5;
  transition:background-color .5s ease,color .5s ease;
  -webkit-font-smoothing:antialiased; overflow-x:hidden;
}
::selection{background:var(--accent);color:var(--on-accent)}
a{color:inherit;text-decoration:none}
button{font-family:inherit;color:inherit;background:none;border:none;cursor:pointer}
img{display:block;max-width:100%}
:focus-visible{outline:2px solid var(--accent);outline-offset:3px}
::-webkit-scrollbar{width:10px}
::-webkit-scrollbar-track{background:var(--bg)}
::-webkit-scrollbar-thumb{background:var(--muted);border:3px solid var(--bg)}

.wrap{max-width:1520px;margin:0 auto;padding:0 clamp(16px,4vw,48px)}
.mono{font-family:var(--font-m);font-size:11px;letter-spacing:.08em;text-transform:uppercase}
.muted{color:var(--muted)}

/* шум */
.noise{position:fixed;inset:0;z-index:60;pointer-events:none;opacity:.055;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='240'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}

/* ============ ШАПКА ============ */
header{position:fixed;top:0;left:0;right:0;z-index:50;height:var(--head-h);
  background:color-mix(in srgb,var(--bg) 88%,transparent);
  backdrop-filter:blur(8px);border-bottom:1px solid var(--line);transition:background-color .5s ease}
.head-in{height:var(--head-h);display:flex;align-items:center;gap:16px}
.mark{display:flex;align-items:center;justify-content:center;width:40px;height:40px;
  font-weight:900;font-size:13px;letter-spacing:-.02em;flex:none;
  background:var(--ink);color:var(--bg);transition:all .4s ease}
.brand-name{font-weight:900;font-size:18px;letter-spacing:-.02em}
.brand-name sup{font-size:9px}
nav.main{display:flex;gap:22px;margin-left:28px}
nav.main a{font-family:var(--font-m);font-size:11px;letter-spacing:.1em;text-transform:uppercase;position:relative;padding:4px 0}
nav.main a::after{content:"";position:absolute;left:0;bottom:0;height:2px;width:0;background:var(--accent);transition:width .3s ease}
nav.main a:hover::after{width:100%}
.head-right{margin-left:auto;display:flex;align-items:center;gap:14px}
.clock{font-family:var(--font-m);font-size:11px;letter-spacing:.08em;color:var(--muted)}
.burger{display:none;width:40px;height:40px;position:relative}
.burger span{position:absolute;left:9px;right:9px;height:2px;background:var(--ink);transition:all .3s ease}
.burger span:nth-child(1){top:14px}.burger span:nth-child(2){top:24px}
body.nav-open .burger span:nth-child(1){top:19px;transform:rotate(45deg)}
body.nav-open .burger span:nth-child(2){top:19px;transform:rotate(-45deg)}

/* оверлей-меню */
.ov{position:fixed;inset:0;z-index:45;background:var(--ink);color:var(--bg);
  transform:translateY(-102%);transition:transform .55s cubic-bezier(.7,0,.2,1);
  display:flex;flex-direction:column;justify-content:center;padding:96px clamp(20px,7vw,90px) 40px}
body.nav-open .ov{transform:translateY(0)}
.ov a{font-weight:900;font-size:clamp(34px,8vw,72px);letter-spacing:-.03em;line-height:1.14;
  opacity:0;transform:translateY(30px);transition:opacity .5s ease,transform .5s ease}
body.nav-open .ov a{opacity:1;transform:none}
.ov a:hover{color:var(--accent)}
.ov a:nth-child(1){transition-delay:.15s}.ov a:nth-child(2){transition-delay:.22s}
.ov a:nth-child(3){transition-delay:.29s}.ov a:nth-child(4){transition-delay:.36s}
.ov-foot{margin-top:auto;display:flex;justify-content:space-between;gap:16px;color:var(--muted)}

/* ============ ХИРО ============ */
.hero{padding-top:calc(var(--head-h) + clamp(28px,5vh,64px));position:relative}
.hero-top{display:flex;justify-content:space-between;gap:16px;border-bottom:1px solid var(--line);padding-bottom:14px}
.hero-grid{display:grid;grid-template-columns:repeat(12,1fr);gap:0;border-bottom:1px solid var(--line)}
.hero-left{grid-column:1/9;padding:clamp(20px,3vw,44px) clamp(0px,2vw,32px) clamp(28px,3.4vw,52px) 0;position:relative}
.hero-title{font-weight:900;font-size:clamp(88px,17.5vw,268px);line-height:.82;letter-spacing:-.045em;display:flex;align-items:flex-start;gap:.02em}
.hero-title .reg{font-size:clamp(14px,1.6vw,24px);font-weight:500;margin-top:.35em}
.tagline{margin-top:clamp(20px,3vw,40px);font-weight:700;font-size:clamp(20px,2.6vw,38px);line-height:1.12;letter-spacing:-.02em;max-width:16em}
.lm{display:block;overflow:hidden}
.lm>span{display:block;transform:translateY(115%);transition:transform .9s cubic-bezier(.2,.7,.2,1)}
.play .lm:nth-child(1)>span{transition-delay:.05s}
.play .lm:nth-child(2)>span{transition-delay:.15s}
.play .lm:nth-child(3)>span{transition-delay:.25s}
.play .lm>span{transform:translateY(0)}
.lede{margin-top:22px;max-width:46ch;color:var(--muted);font-size:15px}
.hero-right{grid-column:9/13;border-left:1px solid var(--line);padding:clamp(20px,2.4vw,36px);display:flex;flex-direction:column;gap:14px}
.frame{border:1px solid var(--line);background:var(--card);padding:10px;position:relative;overflow:hidden}
.frame img{width:100%;aspect-ratio:1/1;object-fit:cover;transition:opacity .45s ease,transform 6s ease;filter:contrast(1.02)}
.frame:hover img{transform:scale(1.04)}
.frame .corner{position:absolute;top:10px;left:10px;background:var(--accent);color:var(--on-accent);font-family:var(--font-m);font-size:10px;letter-spacing:.1em;padding:4px 8px}
.cap{display:flex;justify-content:space-between;gap:10px;font-family:var(--font-m);font-size:11px;letter-spacing:.06em;text-transform:uppercase}
.cap b{color:var(--accent);font-weight:500}
.hero-index{margin-top:auto;display:flex;align-items:baseline;justify-content:space-between;border-top:1px solid var(--line);padding-top:12px}
.hero-index .big{font-weight:900;font-size:clamp(28px,3vw,44px);letter-spacing:-.03em}
.hero-index .big em{font-style:normal;color:var(--accent)}
/* статистика */
.stats{display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid var(--line)}
.stat{padding:clamp(16px,2vw,28px);border-right:1px solid var(--line)}
.stat:last-child{border-right:none}
.stat .num{font-weight:900;font-size:clamp(30px,3.6vw,56px);letter-spacing:-.03em;line-height:1}
.stat .num i{font-style:normal;color:var(--accent)}
.stat .lbl{margin-top:6px}

/* ============ БЕГУЩАЯ СТРОКА ============ */
.marquee{background:var(--accent);color:var(--on-accent);overflow:hidden;border-bottom:1px solid var(--line);transition:background-color .5s ease}
.mq-track{display:flex;width:max-content;animation:mq 26s linear infinite}
.mq-track span{font-family:var(--font-m);font-size:12px;letter-spacing:.14em;text-transform:uppercase;padding:12px 0;white-space:nowrap}
@keyframes mq{to{transform:translateX(-50%)}}
.marquee:hover .mq-track{animation-play-state:paused}

/* ============ СЕКЦИИ ============ */
section{position:relative}
.sec-head{display:flex;align-items:baseline;justify-content:space-between;gap:16px;padding:clamp(20px,2.6vw,36px) 0;border-bottom:1px solid var(--line)}
.sec-head h2{font-weight:900;font-size:clamp(30px,5vw,72px);letter-spacing:-.03em;line-height:1}
.sec-head .idx{font-family:var(--font-m);font-size:12px;color:var(--accent)}

/* манифест */
.manifesto{display:grid;grid-template-columns:4fr 8fr;border-bottom:1px solid var(--line)}
.mani-aside{border-right:1px solid var(--line);padding:clamp(20px,2.4vw,40px)}
.sticky{position:sticky;top:calc(var(--head-h) + 20px)}
.mani-aside .k{font-family:var(--font-m);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--accent)}
.mani-aside .t{margin-top:10px;font-weight:900;font-size:clamp(22px,2.2vw,32px);letter-spacing:-.02em}
.mani-body{padding:clamp(24px,3vw,52px)}
.mani-text{font-weight:700;font-size:clamp(21px,2.7vw,40px);line-height:1.22;letter-spacing:-.02em;max-width:24em}
.mani-text em{font-style:normal;color:var(--accent)}
.mani-note{margin-top:26px;max-width:58ch;color:var(--muted);font-size:15px}

/* работы */
.works{border-bottom:1px solid var(--line)}
.works .watermark{position:absolute;right:-2%;top:8%;z-index:0;font-weight:900;font-size:clamp(120px,24vw,400px);line-height:1;color:transparent;-webkit-text-stroke:1px var(--line);pointer-events:none;user-select:none}
.w-cards{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line);border-bottom:1px solid var(--line);position:relative;z-index:1}
.w-card{background:var(--bg);padding:clamp(16px,2vw,28px);transition:background-color .35s ease}
.w-card:hover{background:var(--card)}
.w-prev{aspect-ratio:16/10;border:1px solid var(--line);overflow:hidden;position:relative;background:var(--bg)}
.w-prev::after{content:"";position:absolute;inset:0;background:var(--accent);mix-blend-mode:multiply;opacity:0;transition:opacity .35s ease}
.w-card:hover .w-prev::after, .w-row:hover .w-prev::after{opacity:.14}
.w-meta{display:flex;justify-content:space-between;align-items:baseline;gap:10px;margin-top:14px}
.w-title{font-weight:900;font-size:clamp(22px,2.4vw,36px);letter-spacing:-.02em}
.w-year{font-family:var(--font-m);font-size:12px;color:var(--muted)}
.w-tags{margin-top:6px;color:var(--muted)}
.w-arrow{display:inline-block;transition:transform .3s ease;color:var(--accent)}
.w-card:hover .w-arrow,.w-row:hover .w-arrow{transform:translate(6px,-6px)}
.w-rows{position:relative;z-index:1}
.w-row{display:grid;grid-template-columns:70px 1fr 150px auto 60px 40px;align-items:center;gap:16px;padding:clamp(16px,2vw,26px) 4px;border-bottom:1px solid var(--line);transition:background-color .3s ease,color .3s ease;cursor:pointer}
.w-row:last-child{border-bottom:none}
.w-row .n{font-family:var(--font-m);font-size:12px;color:var(--muted)}
.w-row .w-prev{aspect-ratio:16/9;width:150px}
.w-row:hover{background:var(--ink);color:var(--bg)}
.w-row:hover .n,.w-row:hover .w-year,.w-row:hover .w-tags{color:var(--bg)}
.w-row:hover .w-arrow{color:var(--accent)}

/* паттерны */
.pat-a{background:repeating-linear-gradient(45deg,var(--accent) 0 10px,transparent 10px 26px)}
.pat-b{background:radial-gradient(var(--ink) 1.6px,transparent 1.7px);background-size:14px 14px}
.pat-c{background:conic-gradient(var(--ink) 25%,transparent 0 50%,var(--ink) 0 75%,transparent 0);background-size:36px 36px}
.pat-d{background:repeating-radial-gradient(circle at 30% 60%,var(--accent) 0 3px,transparent 3px 22px)}
.pat-e{background:repeating-linear-gradient(90deg,var(--ink) 0 4px,transparent 4px 24px)}
.pat-f{background:linear-gradient(135deg,var(--accent) 0 50%,transparent 50%),repeating-linear-gradient(0deg,var(--ink) 0 2px,transparent 2px 18px)}

/* услуги */
.services{display:grid;grid-template-columns:4fr 8fr;border-bottom:1px solid var(--line)}
.serv-aside{border-right:1px solid var(--line);padding:clamp(20px,2.4vw,40px)}
.serv-list{list-style:none}
.serv-list li{display:grid;grid-template-columns:64px 1fr;gap:18px;padding:clamp(18px,2.2vw,30px) clamp(16px,2.4vw,40px);border-bottom:1px solid var(--line);transition:padding-left .3s ease,background-color .3s ease}
.serv-list li:last-child{border-bottom:none}
.serv-list li:hover{background:var(--card);padding-left:calc(clamp(16px,2.4vw,40px) + 12px)}
.serv-list .sn{font-family:var(--font-m);font-size:12px;color:var(--accent);padding-top:6px}
.serv-list h3{font-weight:900;font-size:clamp(20px,2.2vw,32px);letter-spacing:-.02em}
.serv-list p{margin-top:6px;color:var(--muted);max-width:52ch;font-size:14px}

/* клиенты / награды */
.duo{display:grid;grid-template-columns:1fr 1fr;border-bottom:1px solid var(--line)}
.duo>div{padding:clamp(20px,2.4vw,40px)}
.duo>div:first-child{border-right:1px solid var(--line)}
.duo h4{font-family:var(--font-m);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--accent);margin-bottom:18px}
.clients{display:grid;grid-template-columns:repeat(3,1fr);border-top:1px solid var(--line);border-left:1px solid var(--line)}
.clients span{border-right:1px solid var(--line);border-bottom:1px solid var(--line);padding:14px 10px;text-align:center;font-family:var(--font-m);font-size:11px;letter-spacing:.1em;transition:all .25s ease;cursor:default}
.clients span:hover{background:var(--accent);color:var(--on-accent)}
.awards{list-style:none}
.awards li{display:flex;justify-content:space-between;gap:14px;padding:12px 0;border-bottom:1px solid var(--line);font-size:14px}
.awards li:last-child{border-bottom:none}
.awards .yr{font-family:var(--font-m);font-size:11px;color:var(--muted)}
.awards li:hover .aw-name{color:var(--accent)}

/* контакт */
.contact{padding:clamp(48px,8vh,110px) 0;border-bottom:1px solid var(--line);overflow:hidden}
.contact .kick{font-family:var(--font-m);font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--accent)}
.contact h2{margin-top:16px;font-weight:900;font-size:clamp(44px,9.5vw,150px);letter-spacing:-.04em;line-height:.95}
.contact h2 .dot{color:var(--accent)}
.mail-row{margin-top:clamp(24px,3vw,44px);display:flex;flex-wrap:wrap;gap:14px;align-items:center}
.mail-btn{font-family:var(--font-m);font-size:13px;letter-spacing:.06em;border:1px solid var(--ink);padding:14px 22px;position:relative;overflow:hidden;transition:color .3s ease}
.mail-btn::before{content:"";position:absolute;inset:0;background:var(--accent);transform:translateY(101%);transition:transform .35s cubic-bezier(.2,.7,.2,1);z-index:-1}
.mail-btn:hover{color:var(--on-accent);border-color:var(--accent)}
.mail-btn:hover::before{transform:translateY(0)}
.socials{display:flex;gap:18px}
.socials a{font-family:var(--font-m);font-size:11px;letter-spacing:.1em;text-transform:uppercase;border-bottom:1px solid var(--line);padding-bottom:2px;transition:all .25s}
.socials a:hover{color:var(--accent);border-color:var(--accent)}

/* футер */
footer{padding:26px 0 34px}
.foot{display:grid;grid-template-columns:2fr 1fr 1fr 1.4fr;gap:24px}
.foot h5{font-family:var(--font-m);font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);margin-bottom:10px}
.foot a{display:block;font-size:13px;padding:3px 0}
.foot a:hover{color:var(--accent)}
.foot .f-brand{font-weight:900;font-size:20px;letter-spacing:-.02em}
.foot .small{font-size:12px;color:var(--muted)}
.top-btn{margin-top:12px;font-family:var(--font-m);font-size:11px;letter-spacing:.1em;border:1px solid var(--line);padding:8px 14px;transition:all .3s}
.top-btn:hover{background:var(--ink);color:var(--bg)}

/* reveal */
.rv{opacity:0;transform:translateY(26px);transition:opacity .8s ease,transform .8s cubic-bezier(.2,.7,.2,1)}
.rv.in{opacity:1;transform:none}

/* ============ АДАПТИВ ============ */
@media(max-width:1080px){
  nav.main{display:none}
  .burger{display:block}
  .hero-left{grid-column:1/13;padding-right:0}
  .hero-right{grid-column:1/13;border-left:none;border-top:1px solid var(--line)}
  .manifesto,.services{grid-template-columns:1fr}
  .mani-aside,.serv-aside{border-right:none;border-bottom:1px solid var(--line)}
  .sticky{position:static}
  .duo{grid-template-columns:1fr}
  .duo>div:first-child{border-right:none;border-bottom:1px solid var(--line)}
  .w-row{grid-template-columns:44px 1fr 40px;grid-template-areas:"n t a" "n p a" "n y a"}
  .w-row .n{grid-area:n}.w-row .w-title{grid-area:t}.w-row .w-arrow{grid-area:a}
  .w-row .w-prev{grid-area:p;width:100%;max-width:260px;margin-top:8px}
  .w-row .w-tags{grid-area:y}
  .w-row .w-year{display:none}
  .foot{grid-template-columns:1fr 1fr}
}
@media(max-width:640px){
  .clock{display:none}
  .stats{grid-template-columns:1fr 1fr}
  .stat:nth-child(2){border-right:none}
  .stat:nth-child(1),.stat:nth-child(2){border-bottom:1px solid var(--line)}
  .w-cards{grid-template-columns:1fr}
  .clients{grid-template-columns:repeat(2,1fr)}
  .foot{grid-template-columns:1fr}
  .hero-title{font-size:clamp(76px,24vw,140px)}
}

/* reduced motion */
@media (prefers-reduced-motion: reduce){
  html{scroll-behavior:auto}
  *,*::before,*::after{animation:none!important;transition:none!important}
  .rv{opacity:1;transform:none}
  .lm>span{transform:none}
}
`

// ── HTML body ─────────────────────────────────────────────────────────────────
const ORGANIC_PORTFOLIO_HTML_BODY = `
<div class="noise" aria-hidden="true"></div>

<!-- ШАПКА -->
<header>
  <div class="wrap head-in">
    <a href="#top" class="mark" aria-label="BST — на главную">BST</a>
    <span class="brand-name">BST<sup>®</sup></span>
    <nav class="main" aria-label="Основная навигация">
      <a href="#works">Работы</a>
      <a href="#studio">Студия</a>
      <a href="#services">Услуги</a>
      <a href="#contact">Контакт</a>
    </nav>
    <div class="head-right">
      <span class="clock" id="clock">00:00:00</span>
      <button class="burger" id="burger" aria-label="Меню"><span></span><span></span></button>
    </div>
  </div>
</header>

<!-- ОВЕРЛЕЙ МЕНЮ -->
<div class="ov" id="ov">
  <a href="#works">Работы</a>
  <a href="#studio">Студия</a>
  <a href="#services">Услуги</a>
  <a href="#contact">Контакт</a>
  <div class="ov-foot mono"><span>BST® Студия</span><span>BST® / 2025</span></div>
</div>

<main id="top">
  <!-- ХИРО -->
  <section class="hero wrap">
    <div class="hero-top mono muted">
      <span id="heroKicker">Студия органической айдентики · с 2014</span>
      <span>Портфолио · 2014—2025</span>
      <span id="heroCoords">52.52° N, 13.40° E</span>
    </div>
    <div class="hero-grid">
      <div class="hero-left">
        <h1 class="hero-title"><span id="brandTitle">BST</span><span class="reg">®</span></h1>
        <p class="tagline">
          <span class="lm"><span id="tagline0">Форма из природы.</span></span>
          <span class="lm"><span id="tagline1">Тактильный знак.</span></span>
          <span class="lm"><span id="tagline2">Живой бренд.</span></span>
        </p>
        <p class="lede" id="lede">Органика форм, тепло материала и один земляной акцент. Айдентика для брендов, которые растут честно.</p>
      </div>
      <aside class="hero-right">
        <div class="frame">
          <span class="corner" data-t="heroFrameLabel">Кейс 01</span>
          <img data-img="heroImg" src="https://images.unsplash.com/photo-1553361371-9b22f78e8b1d?w=600&q=80" alt="Органическая упаковка студии BST">
        </div>
        <div class="cap"><b data-t="heroCapName">Terra Botanica</b><span data-t="heroCapDesc">Айдентика · Упаковка · Этикетка — 2025</span></div>
        <div class="hero-index">
          <span class="mono muted">Портфолио</span>
          <span class="big"><em>10</em>+ лет</span>
        </div>
      </aside>
    </div>
    <div class="stats">
      <div class="stat rv"><div class="num" data-t="stat1V">10<i>+</i></div><div class="lbl mono muted" data-t="stat1L">лет практики</div></div>
      <div class="stat rv"><div class="num" data-t="stat2V">180<i>+</i></div><div class="lbl mono muted" data-t="stat2L">органических брендов</div></div>
      <div class="stat rv"><div class="num" data-t="stat3V">14</div><div class="lbl mono muted" data-t="stat3L">международных наград</div></div>
      <div class="stat rv"><div class="num" data-t="stat4V">08</div><div class="lbl mono muted" data-t="stat4L">стран присутствия</div></div>
    </div>
  </section>

  <!-- БЕГУЩАЯ СТРОКА -->
  <div class="marquee" aria-hidden="true">
    <div class="mq-track">
      <span>ОРГАНИКА — ФОРМА — ТАКТИЛЬНОСТЬ ● ЗЕМЛЯ — ШРИФТ — МАТЕРИАЛ ● ПРИРОДА — ЗНАК — ЖИЗНЬ ●&nbsp;</span>
      <span>ОРГАНИКА — ФОРМА — ТАКТИЛЬНОСТЬ ● ЗЕМЛЯ — ШРИФТ — МАТЕРИАЛ ● ПРИРОДА — ЗНАК — ЖИЗНЬ ●&nbsp;</span>
    </div>
  </div>

  <!-- МАНИФЕСТ -->
  <section class="manifesto" id="studio">
    <aside class="mani-aside">
      <div class="sticky">
        <div class="k">01 / Студия</div>
        <div class="t">Форма из природы</div>
      </div>
    </aside>
    <div class="mani-body">
      <p class="mani-text rv" id="studioText">BST — студия для органических брендов. Мы верим, что лучший знак можно потрогать: <em>шероховатая текстура</em>, тёплое тиснение, натуральная палитра. Один голос — от упаковки до витрины.</p>
      <p class="mani-note rv" id="studioNote">Каждый проект начинается с материала: бумага, воск, ткань. Потом приходит форма — и она остаётся живой.</p>
    </div>
  </section>

  <!-- РАБОТЫ -->
  <section class="works" id="works">
    <div class="watermark" aria-hidden="true">BST</div>
    <div class="wrap">
      <div class="sec-head">
        <h2 data-t="worksTitle">Избранные работы</h2>
        <span class="idx" data-t="worksIdx">(06) / 2023—2025</span>
      </div>
    </div>
    <div class="wrap">
      <div class="w-cards">
        <article class="w-card rv">
          <div class="w-prev pat-a"></div>
          <div class="w-meta"><span class="w-title" data-t="p1Title">TERRA BOTANICA</span><span class="w-year">2025</span></div>
          <div class="w-tags mono muted" data-t="p1Tags">Бренд / Упаковка / Этикетка <span class="w-arrow">↗</span></div>
        </article>
        <article class="w-card rv">
          <div class="w-prev pat-b"></div>
          <div class="w-meta"><span class="w-title" data-t="p2Title">MORA HERBS</span><span class="w-year">2025</span></div>
          <div class="w-tags mono muted" data-t="p2Tags">Айдентика / Веб / Система <span class="w-arrow">↗</span></div>
        </article>
      </div>
      <div class="w-rows">
        <div class="w-row rv"><span class="n">03</span><span class="w-title" data-t="p3Title">БИОКРАФТ</span><div class="w-prev pat-c"></div><span class="w-tags mono muted" data-t="p3Tags">Ребрендинг / Навигация</span><span class="w-year">2024</span><span class="w-arrow">↗</span></div>
        <div class="w-row rv"><span class="n">04</span><span class="w-title" data-t="p4Title">GREEN RITUAL</span><div class="w-prev pat-d"></div><span class="w-tags mono muted" data-t="p4Tags">Веб / Дизайн-система</span><span class="w-year">2024</span><span class="w-arrow">↗</span></div>
        <div class="w-row rv"><span class="n">05</span><span class="w-title" data-t="p5Title">СЕЗОН</span><div class="w-prev pat-e"></div><span class="w-tags mono muted" data-t="p5Tags">Бренд / Печать</span><span class="w-year">2024</span><span class="w-arrow">↗</span></div>
        <div class="w-row rv"><span class="n">06</span><span class="w-title" data-t="p6Title">VOLNA</span><div class="w-prev pat-f"></div><span class="w-tags mono muted" data-t="p6Tags">Диджитал / Упаковка</span><span class="w-year">2023</span><span class="w-arrow">↗</span></div>
      </div>
    </div>
  </section>

  <!-- УСЛУГИ -->
  <section class="services" id="services">
    <aside class="serv-aside">
      <div class="sticky">
        <div class="k" style="font-family:var(--font-m);font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--accent)">02 / Услуги</div>
        <div class="t" style="margin-top:10px;font-weight:900;font-size:clamp(22px,2.2vw,32px);letter-spacing:-.02em">Что мы делаем</div>
      </div>
    </aside>
    <ul class="serv-list">
      <li class="rv"><span class="sn">/01</span><div><h3 data-t="serv1Title">Органическая айдентика</h3><p data-t="serv1Desc">Логотип, фирменный стиль, гайдлайны. Знак, который живёт в природе: тиснение, штамп, этикетка, ткань.</p></div></li>
      <li class="rv"><span class="sn">/02</span><div><h3 data-t="serv2Title">Упаковка и этикетки</h3><p data-t="serv2Desc">Проектируем для полки: форма бутылки, бумага, краска. Тактильность, которую хочется взять в руки.</p></div></li>
      <li class="rv"><span class="sn">/03</span><div><h3 data-t="serv3Title">Веб и диджитал</h3><p data-t="serv3Desc">Сайты, лендинги, дизайн-системы. Тепло материала переносится в пиксели без потерь.</p></div></li>
      <li class="rv"><span class="sn">/04</span><div><h3 data-t="serv4Title">Арт-дирекшн и съёмка</h3><p data-t="serv4Desc">Концепции, натурные съёмки, иллюстрация, моушен. Природный контекст в каждом кадре.</p></div></li>
      <li class="rv"><span class="sn">/05</span><div><h3 data-t="serv5Title">Стратегия и нейминг</h3><p data-t="serv5Desc">Позиционирование, нейминг, тон-оф-войс. Аутентичная история бренда — от корней.</p></div></li>
    </ul>
  </section>

  <!-- КЛИЕНТЫ / НАГРАДЫ -->
  <section class="duo">
    <div>
      <h4>Клиенты (избранное)</h4>
      <div class="clients">
        <span>TERRA</span><span>MORA HERBS</span><span>БИОКРАФТ</span>
        <span>GREEN RITUAL</span><span>СЕЗОН</span><span>VOLNA</span>
        <span>ROOTS</span><span>ЭКОЛАБ</span><span>BLOSSOM</span>
        <span>ЛЕСНОЙ</span><span>SOLUM</span><span>BLOOM</span>
      </div>
    </div>
    <div>
      <h4>Признание</h4>
      <ul class="awards">
        <li><span class="aw-name">Red Dot — Packaging Design</span><span class="yr">2024</span></li>
        <li><span class="aw-name">Pentawards — Silver, Organic</span><span class="yr">2024</span></li>
        <li><span class="aw-name">Awwwards — Site of the Day ×2</span><span class="yr">2023–24</span></li>
        <li><span class="aw-name">Dieline — Best in Category</span><span class="yr">2023</span></li>
      </ul>
    </div>
  </section>

  <!-- КОНТАКТ -->
  <section class="contact wrap" id="contact">
    <div class="kick">03 / Контакт</div>
    <h2 class="rv" id="ctaTitle">Начнём<br>разговор<span class="dot">.</span></h2>
    <div class="mail-row rv">
      <button class="mail-btn" id="copyMail">hello@bst.studio — копировать</button>
      <div class="socials">
        <a href="#" rel="noopener">Behance</a>
        <a href="#" rel="noopener">Instagram</a>
        <a href="#" rel="noopener">Telegram</a>
      </div>
    </div>
  </section>
</main>

<!-- ФУТЕР -->
<footer>
  <div class="wrap foot">
    <div>
      <div class="f-brand">BST<sup style="font-size:9px">®</sup></div>
      <p class="small" style="margin-top:8px">Студия органической айдентики.<br>Природа, материал, тактильность.</p>
      <button class="top-btn" id="toTop">↑ НАВЕРХ</button>
    </div>
    <div>
      <h5>Навигация</h5>
      <a href="#works">Работы</a><a href="#studio">Студия</a><a href="#services">Услуги</a><a href="#contact">Контакт</a>
    </div>
    <div>
      <h5>Соцсети</h5>
      <a href="#">Behance</a><a href="#">Instagram</a><a href="#">Telegram</a>
    </div>
    <div>
      <h5>Колофон</h5>
      <p class="small">Текст: Archivo · Моно: IBM Plex Mono</p>
      <p class="small">© 2025 BST®. Сделано с любовью к природе.</p>
    </div>
  </div>
</footer>
`

// ── Engine JS ─────────────────────────────────────────────────────────────────
function organicPortfolioEngineJs(mode: 'canvas' | 'preview'): string {
  return `
(function(){
  "use strict";
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- часы ---------- */
  var clock=document.getElementById("clock");
  function two(n){ return (n<10?"0":"")+n; }
  function tickClock(){
    var d=new Date();
    if(clock) clock.textContent=two(d.getHours())+":"+two(d.getMinutes())+":"+two(d.getSeconds());
  }
  if(clock){ tickClock(); setInterval(tickClock,1000); }

  /* ---------- reveal on scroll ---------- */
  var io=new IntersectionObserver(function(entries){
    entries.forEach(function(en){
      if(en.isIntersecting){ en.target.classList.add("in"); io.unobserve(en.target); }
    });
  },{threshold:.12});
  document.querySelectorAll(".rv").forEach(function(el,i){
    el.style.transitionDelay=(i%4)*0.08+"s";
    io.observe(el);
  });

  /* ---------- tagline play ---------- */
  window.addEventListener("load", function(){ document.querySelector(".tagline") && document.querySelector(".tagline").classList.add("play"); });

  /* ---------- меню ---------- */
  var burger=document.getElementById("burger"), ov=document.getElementById("ov");
  if(burger) burger.addEventListener("click", function(){ document.body.classList.toggle("nav-open"); });
  if(ov) ov.querySelectorAll("a").forEach(function(a){
    a.addEventListener("click", function(){ document.body.classList.remove("nav-open"); });
  });

  /* ---------- копирование почты ---------- */
  var copyBtn=document.getElementById("copyMail");
  if(copyBtn){
    var copyOrig=copyBtn.textContent;
    copyBtn.addEventListener("click", function(){
      function done(){ copyBtn.textContent="Скопировано ✓"; setTimeout(function(){ copyBtn.textContent=copyOrig; },1600); }
      if(navigator.clipboard){ navigator.clipboard.writeText("hello@bst.studio").then(done).catch(done); }
      else{ done(); }
    });
  }

  /* ---------- наверх ---------- */
  var toTop=document.getElementById("toTop");
  if(toTop) toTop.addEventListener("click", function(){
    window.scrollTo({top:0, behavior: reduced ? "auto" : "smooth"});
  });
})();
${mode === 'canvas' ? `(function(){
  document.querySelectorAll('.rv,.reveal,[data-rv]').forEach(function(el){el.classList.add('in');});
  document.querySelectorAll('.morph').forEach(function(el){el.style.animationPlayState='paused';});
  document.body.classList.add('loaded','is-ready');
  document.querySelector('.tagline') && document.querySelector('.tagline').classList.add('play');
  document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(a)e.preventDefault();},true);
  document.querySelectorAll('form').forEach(function(f){f.addEventListener('submit',function(e){e.preventDefault();});});
})();` : ''}
`
}

// ── Public API ────────────────────────────────────────────────────────────────
export function getOrganicPortfolioPageHtml(_data: OrganicPortfolioPageData, mode: 'canvas' | 'preview'): string {
  return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>BST Portfolio</title>
<link rel="preconnect" href="https://cdn.jsdelivr.net">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo@5.2.5/latin-400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo@5.2.5/latin-500.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo@5.2.5/latin-700.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/archivo@5.2.5/latin-900.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5.2.5/latin-400.css">
<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/@fontsource/ibm-plex-mono@5.2.5/latin-500.css">
<style>${ORGANIC_PORTFOLIO_CSS}</style>
</head>
<body>
${ORGANIC_PORTFOLIO_HTML_BODY}
<script>${organicPortfolioEngineJs(mode)}</script>
</body>
</html>`
}
